/**
 * Anonymous usage events: which steps people reach and which channel they came from.
 * Sends only an event name, a random device id, the first-touch source and the screen.
 * Never kick positions/times, notes, names or due dates. No-op in demo mode.
 *
 * Source: `?from=tiktok` (or utm_source) on the first visit; otherwise a guess from the
 * referrer; otherwise 'direct'. Kept for the life of the device (first touch).
 */
import { config } from './config';

const DEVICE_KEY = 'bk.device';
const SOURCE_KEY = 'bk.source';
const ONCE_KEY = 'bk.once';

const store = {
  get(k: string): string | null { try { return localStorage.getItem(k); } catch { return null; } },
  set(k: string, v: string) { try { localStorage.setItem(k, v); } catch { /* private mode: still works for this visit */ } }
};

function uuid(): string {
  if (crypto.randomUUID) return crypto.randomUUID();
  return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, c => (+c ^ (crypto.getRandomValues(new Uint8Array(1))[0]! & (15 >> (+c / 4)))).toString(16));
}

let device = store.get(DEVICE_KEY);
if (!device) { device = uuid(); store.set(DEVICE_KEY, device); }

const clean = (s: string | null | undefined) => (s ?? '').toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 24) || null;

function referrerSource(): string | null {
  try {
    const host = document.referrer ? new URL(document.referrer).hostname : '';
    if (!host || host.endsWith(location.hostname)) return null;
    const known: [RegExp, string][] = [[/tiktok/, 'tiktok'], [/facebook|fb\.|messenger/, 'facebook'], [/instagram/, 'instagram'],
      [/line\.me|line-apps/, 'line'], [/google\./, 'google'], [/youtube/, 'youtube'], [/pantip/, 'pantip']];
    return known.find(([re]) => re.test(host))?.[1] ?? clean(host.replace(/^www\./, '').split('.')[0]);
  } catch { return null; }
}

// first touch only: the channel that brought her the first time
let source = store.get(SOURCE_KEY);
const params = new URLSearchParams(location.search);
const fromParam = clean(params.get('from') ?? params.get('utm_source'));
if (!source) { source = fromParam ?? referrerSource() ?? 'direct'; store.set(SOURCE_KEY, source); }
if (params.has('from') || params.has('utm_source')) {
  params.delete('from'); params.delete('utm_source');
  const q = params.toString();
  try { history.replaceState(history.state, '', location.pathname + (q ? `?${q}` : '') + location.hash); } catch { /* ignore */ }
}

let currentScreen: string | null = null;
let queue: { device_id: string; event: string; source: string | null; screen: string | null }[] = [];
let timer = 0;

async function flush(): Promise<void> {
  timer = 0;
  if (!queue.length) return;
  const rows = queue; queue = [];
  try {
    const { supabase } = await import('./cloud');
    const { error } = await (await supabase()).from('app_events').insert(rows);
    if (error) console.warn('[track]', error.message);
  } catch (e) { console.warn('[track] not sent', e); }   // analytics must never break the app
}

function send(event: string) {
  if (!config.cloud) return;
  queue.push({ device_id: device!, event, source, screen: currentScreen });
  if (queue.length >= 20) void flush();
  else if (!timer) timer = window.setTimeout(() => void flush(), 2000);
}

function onceSet(): Set<string> {
  try { return new Set(JSON.parse(store.get(ONCE_KEY) ?? '[]') as string[]); } catch { return new Set(); }
}

addEventListener('pagehide', () => void flush());
document.addEventListener('visibilitychange', () => { if (document.hidden) void flush(); });

export const track = {
  get source() { return source; },
  setScreen(id: string) { currentScreen = clean(id)?.replace(/-/g, '_') ?? null; },
  /** Every time it happens. */
  event(name: string) { send(name); },
  /** Only the first time on this device (funnel steps). */
  once(name: string) {
    const s = onceSet(); if (s.has(name)) return;
    s.add(name); store.set(ONCE_KEY, JSON.stringify([...s])); send(name);
  },
  /** At most once per day on this device (active days). */
  daily(name: string) {
    const key = `${name}@${new Date().toISOString().slice(0, 10)}`;
    const s = onceSet(); if (s.has(key)) return;
    for (const k of [...s]) if (k.startsWith(`${name}@`)) s.delete(k);
    s.add(key); store.set(ONCE_KEY, JSON.stringify([...s])); send(name);
  }
};
